import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { AUTH_REQUEST_STATE_KEY, isRequiredAuthRequest } from '@/constants/authRequest';
import useIsFirstRender from '@/hooks/useIsFirstRender';
import useStateHandler from '@/hooks/useStateHandler';
import SuarteName from '@/shared-components/icons/components/public/SuarteName';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import { UNREGISTERED_POPUP_OPEN_LOCK_KEY, UNREGISTERED_POPUP_PRESENTED_EVENT, UNREGISTERED_POPUP_PRESENTED_KEY } from '@/shared-components/popups/unregistered-user-popup/constants';

import PopupToBottomSheet from '../components/PopupToBottomSheet';

import AuthForgotPassword from './components/AuthForgotPassword';
import AuthLogin from './components/AuthLogin';
import AuthNavigation from './components/AuthNavigation';
import AuthRegularSignup from './components/AuthRegularSignup';
import AuthSignupSelector from './components/AuthSignupSelector';
import AuthArtistSignup from './auth-artist-signup';

import './index.css';

const isBrowser = typeof window !== 'undefined';

function AuthPopupContent({ close, page = "sign-up", onTabChange, allowClose = true }) {
  const [currentPage, setCurrentPage] = useState(page);

  useEffect(() => {
    onTabChange(currentPage);
  }, []);

  const changeTab = (page) => {
    setCurrentPage(page);
    onTabChange(page);
  };

  const showNavigation = currentPage !== "sign-up-artist";

  return (
    <>
      <div className="auth-popup-content">
        <div className="auth-popup-content__header">
          <SuarteName className="auth-popup-content__header-suarte-name"/>
          {allowClose && (
            <XIcon className="auth-popup-content__header-close" onClick={close}/>
          )}
        </div>
        {
          showNavigation && <AuthNavigation currentPage={currentPage} setCurrentPage={changeTab}/>
        }

        <div className="auth-popup-content__wrap remove-scrollbar">
          {
            currentPage === "login" ? 
              <AuthLogin setCurrentPage={changeTab} close={close}/>
              : currentPage === "forgot" ? 
                <AuthForgotPassword/>
                : currentPage === "sign-up-selector" ? 
                  <AuthSignupSelector setCurrentPage={changeTab}/>
                  : currentPage === "sign-up" ? 
                    <AuthRegularSignup close={close} setCurrentPage={changeTab} />
                    :
                    <AuthArtistSignup setCurrentPage={changeTab} />
          }
        </div>
      </div>
    </>
  );
}

export default function AuthPopup() {
  const { state, stateHandler } = useStateHandler();
  const isFirstRender = useIsFirstRender();
  const location = useLocation();
  const navigate = useNavigate();
  const isResetPasswordRoute = location.pathname === "/reset-password";
  const authRequest = state.authRequest;
  const hasRequiredAuthRequest = isRequiredAuthRequest(authRequest);
  const [hasMandatoryLock, setHasMandatoryLock] = useState(() => {
    if (!isBrowser) {
      return false;
    }
    return localStorage.getItem(UNREGISTERED_POPUP_PRESENTED_KEY) === 'true';
  });

  useEffect(() => {
    if (!isBrowser) {
      setHasMandatoryLock(false);
      return undefined;
    }

    const syncLock = () => {
      setHasMandatoryLock(localStorage.getItem(UNREGISTERED_POPUP_PRESENTED_KEY) === 'true');
    };

    syncLock();
    const handleStorage = (event) => {
      if (event.key === UNREGISTERED_POPUP_PRESENTED_KEY) {
        setHasMandatoryLock(event.newValue === 'true');
      }
    };

    window.addEventListener(UNREGISTERED_POPUP_PRESENTED_EVENT, syncLock);

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(UNREGISTERED_POPUP_PRESENTED_EVENT, syncLock);
    };
  }, []);

  useEffect(() => {
    if (!isBrowser) {
      return;
    }
    setHasMandatoryLock(localStorage.getItem(UNREGISTERED_POPUP_PRESENTED_KEY) === 'true');
  }, [state.action_denied, state.showAuthPopup, state.user_session]);

  const isMandatoryAuth = useMemo(
    () => !state.user_session && hasMandatoryLock && !isResetPasswordRoute,
    [hasMandatoryLock, state.user_session, isResetPasswordRoute],
  );

  useEffect(() => {
    if (!isBrowser || isResetPasswordRoute) {
      return;
    }

    const mandatoryLockActive = localStorage.getItem(UNREGISTERED_POPUP_OPEN_LOCK_KEY) === 'true';
    if (isMandatoryAuth && !mandatoryLockActive && state.action_denied !== "USER_ACCOUNT_REQUIRED") {
      if (!state.unregisteredUser) {
        stateHandler.set("unregisteredUser", "guest_rest");
      }
      stateHandler.set("action_denied", "USER_ACCOUNT_REQUIRED");
    }
  }, [isMandatoryAuth, isResetPasswordRoute, location.pathname, location.search, state.action_denied, state.unregisteredUser, stateHandler]);

  useEffect(() => {
    if(!isFirstRender && state.action_denied === "USER_ACCOUNT_REQUIRED" && !isMandatoryAuth) {
      stateHandler.set("action_denied", null); /** Closes the popup in case we navigated to another page */
    }
    
  }, [isFirstRender, isMandatoryAuth, location.pathname, state.action_denied, stateHandler]);

  useEffect(() => {
    if (!isBrowser || !isResetPasswordRoute) {
      return;
    }

    if (state.action_denied === "USER_ACCOUNT_REQUIRED") {
      stateHandler.set("action_denied", null);
    }
  }, [isResetPasswordRoute, state.action_denied, stateHandler]);

  const shouldShowMandatory = !isResetPasswordRoute && state.action_denied === "USER_ACCOUNT_REQUIRED";
  const shouldBlockClose = !state.user_session && (isMandatoryAuth || hasRequiredAuthRequest);
  const shouldOpenPopup = shouldShowMandatory || !!state.showAuthPopup || !!authRequest;

  const closeAuthSection = useCallback((redirect = true) => {
    if (shouldBlockClose) {
      return;
    }

    stateHandler.set("action_denied", null);
    stateHandler.set("showAuthPopup", undefined);
    stateHandler.remove(AUTH_REQUEST_STATE_KEY);
    stateHandler.set("unregisteredUser", null);

    // if we are in login or sign-up page and we close the modal and redirect to main page
    if(redirect && (location.pathname === "/login" || location.pathname === "/sign-up")) {
      navigate("/", { replace: true });
    }
  }, [location.pathname, navigate, shouldBlockClose, stateHandler]);

  useEffect(() => {
    if(state.user_session) {
      closeAuthSection(false);
    }
  }, [closeAuthSection, state.user_session]);

  const claimEmailParam = new URLSearchParams(location.search).get("claim_email");
  const requestedAuthPage = authRequest?.page || state.showAuthPopup;
  const authPage = claimEmailParam && requestedAuthPage === "sign-up"
    ? "sign-up-artist"
    : (requestedAuthPage || undefined);

  const isHome = (page) => page === "/" || page === "/login" || page === "/sign-up";

  const onTabChange = (tab) => {
    if(isHome(location.pathname)) {
      // if we are in one of this pages, we redirect to the new page
      if(tab === "sign-up" || tab === "login") {
        navigate(tab, { replace: true });
      }
    }
  };

  return (
    <>
      <PopupToBottomSheet 
        className="auth-popup-container" 
        open={shouldOpenPopup} 
        close={closeAuthSection} 
        popupOptions={{ opacity: true }}
      >
        <AuthPopupContent
          close={closeAuthSection}
          page={authPage}
          onTabChange={onTabChange}
          allowClose={!shouldBlockClose}
        />
      </PopupToBottomSheet>
    </>
  );
}
